import { useTranslation } from 'react-i18next'
import { FileText, Table2 } from '@digital-twins/design-system/icons'
import { LibraryCollectionItem } from '@digital-twins/shared-types'

import { CollectionCardLayout, CollectionItemCard, CoverChip, MetaDot, MetaValue } from '@/components/library/CollectionItemCard'
import { coverTone } from '@/components/library/LibraryItemCover'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { MarketLabel } from '@/components/ui/MarketLabel'
import { displayKnowledgeTypeLabel, marketDisplayEntries } from '@/pages/KnowledgeHubPage/libraryHelpers'
import { formatSocialDate } from '@/pages/KnowledgeHubPage/social/socialFormat'
import { ROUTES } from '@/routes/routes'
import { DatasetTopicChips } from './DatasetTopicChips'
import { DatasetDocumentCover } from './DatasetDocumentCover'

export interface CollectionResourceCardProps {
  /** A `document` or `dataset` item. */
  item: LibraryCollectionItem
  /** Test ids are `collection-resource-card-{index}`. */
  index: number
  /** The card's place in its section's grid. */
  layout?: CollectionCardLayout
}

/** The first value of a facet kind, trimmed; null when absent. */
function facetValueOf(item: LibraryCollectionItem, kind: string): string | null {
  const match = item.facets.find((facet) => facet.kind === kind && facet.value.trim().length > 0)
  return match ? match.value.trim() : null
}

/**
 * A document or dataset in the Library card's anatomy, so the same file
 * reads the same on both pages: its cover with the document type chip (its
 * tone dot; the kind when it has no type), the title and summary, then the
 * publisher, the market by name with its flag (or "N markets"), and the
 * year (the date without one). One link to the document.
 */
export function CollectionResourceCard(props: CollectionResourceCardProps) {
  const { item, index, layout = 'standard' } = props

  const { t, i18n } = useTranslation('app')

  const testId = `collection-resource-card-${index}`
  const Icon = item.kind === 'dataset' ? Table2 : FileText
  const kindKey = item.kind === 'dataset' ? 'dataset' : 'document'
  const storedType = facetValueOf(item, 'type')
  const typeLabel = storedType ? displayKnowledgeTypeLabel(storedType) : null
  const brand = facetValueOf(item, 'brand')
  const year = facetValueOf(item, 'year')
  const markets = marketDisplayEntries(
    item.facets.filter((facet) => facet.kind === 'market').map((facet) => facet.value.trim()).filter(Boolean),
  )
  const date = year ? null : formatSocialDate(item.occurredAt, i18n.language)

  const coverChips = typeLabel ? (
    <CoverChip testId={`${testId}-kind`} className="min-w-0 shrink gap-1.5">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${coverTone(typeLabel.toLocaleLowerCase()).swatch}`} aria-hidden="true" />
      <span className="truncate">{typeLabel}</span>
    </CoverChip>
  ) : (
    <CoverChip testId={`${testId}-kind`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {t(`library.collections.resources.kindSingular.${kindKey}`)}
    </CoverChip>
  )

  const marketMeta =
    markets.length === 0 ? null : markets.length === 1 ? (
      <MarketLabel label={markets[0].label} code={markets[0].code} data-testid={`${testId}-market`} />
    ) : (
      <span
        className="inline-flex items-center gap-1.5 whitespace-nowrap"
        title={markets.map((market) => market.label).join(', ')}
        data-testid={`${testId}-market`}
      >
        <CountryFlag code={null} />
        {t('library.marketCount', { count: markets.length })}
      </span>
    )

  const metaParts = [
    brand ? (
      <span
        key="brand"
        className="min-w-0 max-w-[14rem] truncate font-medium text-foreground/80"
        title={brand}
        data-testid={`${testId}-brand`}
      >
        {brand}
      </span>
    ) : null,
    marketMeta ? <span key="market">{marketMeta}</span> : null,
    year ? (
      <MetaValue key="year" testId={`${testId}-year`}>
        {year}
      </MetaValue>
    ) : null,
    date ? (
      <MetaValue key="date" testId={`${testId}-date`}>
        {date}
      </MetaValue>
    ) : null,
  ].filter((part) => part != null)

  const meta =
    metaParts.length > 0
      ? metaParts.flatMap((part, position) => (position === 0 ? [part] : [<MetaDot key={`dot-${position}`} />, part]))
      : null

  return (
    <CollectionItemCard
      item={item}
      href={ROUTES.knowledgeDocument(item.token)}
      testId={testId}
      coverChips={coverChips}
      layout={layout}
      meta={meta}
      details={item.kind === 'dataset' ? <DatasetTopicChips facets={item.facets} /> : undefined}
      coverFallback={item.kind === 'dataset' ? <DatasetDocumentCover /> : undefined}
      cta={t(`library.collections.resources.cta.${kindKey}`)}
    />
  )
}
